/** A fund manager's trade proposal: backtest a set of stock trades on a fund, then finalise them. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { ALL_TRADE_THESES, GET_LATEST_PRICE } from '../queries';
import { CREATE_PORTFOLIO_FROM_HOLDINGS, DELETE_PORTFOLIOS, SAVE_TRADE_THESIS } from '../mutations';
import { BACKTEST_PORTFOLIO_TRADES } from '../mutations/portfolios';
import { EMPTY } from './_shared';

/** Five years, the window the rest of the app reports a composition over. */
export const FUND_BACKTEST_LOOKBACK_DAYS = 1825;

/**
 * The notional corpus the fund's book is built at.
 *
 * Only proportions matter: the backtest answers in rates and ratios, which do
 * not depend on size. A round crore keeps every quantity comfortably above the
 * resolver's "positive quantity" guard even for a 0.1% position.
 */
const NOTIONAL = 10000000;

const fiveYearsAgo = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 5);
  return d.toISOString().slice(0, 10);
};

/** The last published price, from whichever series the security has. */
const latestPrice = (res) => {
  const fund = res?.fundHistory ?? [];
  const stock = res?.stockHistory ?? [];
  const last = fund.length ? fund[fund.length - 1] : stock.length ? stock[stock.length - 1] : null;
  if (!last) return null;
  const px = Number(last.nav ?? last.dayEndNav ?? last.close ?? last.adjustedClose);
  return Number.isFinite(px) && px > 0 ? px : null;
};

/**
 * Backtest a change of composition: `current` weights against `proposed`.
 *
 * WHY A TEMPORARY PORTFOLIO. `backtestPortfolioTrades` prices trades against a
 * portfolio the engine already holds positions for. A mutual fund is not one:
 * pointed at UTI Flexi Cap it answered with an empty current side and a
 * pro-forma made of the trades alone, and at Quant Quantamental "Portfolio not
 * found". So the fund's disclosed book is built as a temporary portfolio at a
 * notional corpus, the proposal is sent as the trades that would get from one
 * weight to the other, and the temporary portfolio is deleted as soon as it has
 * answered — the method the model screen's backtest uses, for the same reason.
 *
 * Weights are FRACTIONS of the fund. A holding that cannot be priced as a
 * security (cash, repo, government paper, derivatives) cannot be built, so it
 * is left out and named in `unpriced` rather than silently dropped; the rest is
 * measured as the fund's priceable book.
 */
export function useCompositionBacktest() {
  const client = useApolloClient();
  const [create] = useMutation(CREATE_PORTFOLIO_FROM_HOLDINGS);
  const [backtest] = useMutation(BACKTEST_PORTFOLIO_TRADES, { fetchPolicy: 'no-cache' });
  const [drop] = useMutation(DELETE_PORTFOLIOS);
  const [state, setState] = useState({ running: false, error: null, result: null, unpriced: EMPTY });
  const temp = useRef(null);

  const tearDown = useCallback((symbol) => (
    symbol ? drop({ variables: { securitySymbols: [symbol] } }).catch(() => {}) : Promise.resolve()
  ), [drop]);

  /* A panel closed mid-run still removes what it built. */
  useEffect(() => () => { tearDown(temp.current); temp.current = null; }, [tearDown]);

  const run = useCallback(async ({ current, proposed, names, label }) => {
    setState({ running: true, error: null, result: null, unpriced: EMPTY });
    let built = null;
    try {
      const symbols = [...new Set([...current.keys(), ...proposed.keys()])];
      const priced = await Promise.all(symbols.map(async (symbol) => {
        try {
          const { data } = await client.query({
            query: GET_LATEST_PRICE, variables: { securitySymbol: symbol }, fetchPolicy: 'cache-first',
          });
          return [symbol, latestPrice(data?.getPriceHistory)];
        } catch { return [symbol, null]; }
      }));
      const prices = new Map(priced);
      const unpriced = symbols.filter((s) => !prices.get(s)).map((s) => names.get(s) || s);

      const holdings = [...current.entries()]
        .filter(([s, w]) => w > 0 && prices.get(s))
        .map(([s, w]) => ({ securitySymbol: s, quantity: (NOTIONAL * w) / prices.get(s), addedDate: fiveYearsAgo() }));
      if (!holdings.length) throw new Error('None of the fund’s holdings could be priced, so there is nothing to measure.');

      const trades = [];
      symbols.forEach((s) => {
        const px = prices.get(s);
        if (!px) return;
        const delta = (proposed.get(s) ?? 0) - (current.get(s) ?? 0);
        if (Math.abs(delta) < 1e-9) return;
        trades.push({ securitySymbol: s, side: delta > 0 ? 'BUY' : 'SELL', quantity: Math.abs((NOTIONAL * delta) / px) });
      });
      if (!trades.length) throw new Error('None of the proposed trades could be priced, so there is nothing to compare.');

      const { data: made } = await create({
        variables: {
          name: `fund_${label || 'proposal'}_backtest_${Date.now().toString(36)}`,
          portfolioType: 'EXTERNAL',
          currency: 'INR',
          computeReturns: true,
          holdings,
        },
      });
      const created = made?.createExternalPortfolioFromHoldings;
      if (!created?.success || !created.securitySymbol) {
        throw new Error(created?.message || 'The fund could not be built for measurement');
      }
      built = created.securitySymbol;
      temp.current = built;

      const { data: out } = await backtest({
        variables: { portfolioSymbol: built, trades, lookbackDays: FUND_BACKTEST_LOOKBACK_DAYS },
      });
      const result = out?.backtestPortfolioTrades;
      if (!result) throw new Error('The backtest returned nothing');
      setState({ running: false, error: null, result, unpriced });
      return result;
    } catch (err) {
      setState({ running: false, error: err.message || 'The backtest could not run', result: null, unpriced: EMPTY });
      return null;
    } finally {
      if (built) { await tearDown(built); if (temp.current === built) temp.current = null; }
    }
  }, [client, create, backtest, tearDown]);

  const reset = useCallback(() => setState({ running: false, error: null, result: null, unpriced: EMPTY }), []);
  return { ...state, run, reset };
}

/** Save one leg's thesis. */
export const useSaveTradeThesis = () => useMutation(SAVE_TRADE_THESIS);

/** The user's active theses — what has been finalised, newest first. */
export function useTradeTheses({ skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(ALL_TRADE_THESES, {
    skip, fetchPolicy: 'cache-and-network', errorPolicy: 'all',
  });
  const rows = [...(data?.getAllTheses ?? EMPTY)]
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  return { rows, loading, error, refetch };
}
